import { createClient } from '@supabase/supabase-js';
import { DebugLogger } from './debug-logger';

// Environment configuration
const isDev = process.env.SUPABASE_ENV === 'dev' || process.env.NODE_ENV === 'development';

// Initialize Supabase client based on environment
const supabaseUrl = isDev 
  ? process.env.NEXT_PUBLIC_SUPABASE_URL_DEV || process.env.NEXT_PUBLIC_SUPABASE_URL
  : process.env.NEXT_PUBLIC_SUPABASE_URL_PROD || process.env.NEXT_PUBLIC_SUPABASE_URL;

const supabaseServiceKey = isDev
  ? process.env.SUPABASE_SERVICE_ROLE_KEY_DEV || process.env.SUPABASE_SERVICE_ROLE_KEY
  : process.env.SUPABASE_SERVICE_ROLE_KEY_PROD || process.env.SUPABASE_SERVICE_ROLE_KEY;

// Create Supabase client with service role key for server-side operations
// For safety in client bundles, do not throw during module import. Export a helper that asserts.
export const supabase = supabaseUrl && supabaseServiceKey
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

export function getServiceSupabaseOrThrow() {
  if (!supabaseUrl || !supabaseServiceKey) {
    const missing = {
      supabaseUrlPresent: !!supabaseUrl,
      supabaseServiceKeyPresent: !!supabaseServiceKey,
    };
    throw new Error(`[Supabase] Missing required environment variables for ${isDev ? 'DEVELOPMENT' : 'PRODUCTION'} environment: ${JSON.stringify(missing)}`);
  }
  return createClient(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });
}

// Export the createClient function for use in other files
export { createClient };

// Log which environment we're using
console.log(`[Supabase] Using ${isDev ? 'DEVELOPMENT' : 'PRODUCTION'} environment: ${supabaseUrl}`);
console.log(`[Supabase] Client initialized: ${!!supabase}`);
console.log(`[Supabase] Service role key configured: ${!!supabaseServiceKey}`);

// Types for our database tables
export interface GoogleTokens {
  id: string;
  email: string;
  access_token: string;
  refresh_token?: string;
  access_token2?: string;
  refresh_token2?: string;
  /**
   * Owned by the ONE OAuth refresh engine
   * (`apps/hub/lib/connections/unifiedtime`). NULL = unknown, which the
   * engine treats as stale. Legacy writers below deliberately NULL it —
   * see `saveGoogleTokensToSupabase`.
   */
  access_token_expires_at?: string | null;
  last_refresh_failure_at?: string | null;
  refresh_attempts?: number;
  refresh_exhausted?: boolean;
  previous_refresh_token?: string | null;
  /**
   * SLOT-2 (the legacy linked second account) mirror of the columns above. The
   * engine addresses this slot as `<providerKey>:<email>:2` and keeps its health
   * strictly separate, so a dead second account can never latch the primary
   * grant. Declared here so the legacy writers below can NULL
   * `access_token_expires_at2` on a slot-2 write — same safe direction as slot 1.
   */
  access_token_expires_at2?: string | null;
  last_refresh_failure_at2?: string | null;
  refresh_attempts2?: number;
  refresh_exhausted2?: boolean;
  previous_refresh_token2?: string | null;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

export interface MsftTokens {
  id: string;
  email: string;
  access_token: string;
  refresh_token?: string;
  access_token2?: string;
  refresh_token2?: string;
  /**
   * Owned by the ONE OAuth refresh engine
   * (`apps/hub/lib/connections/unifiedtime`). NULL = unknown, which the
   * engine treats as stale. Legacy writers below deliberately NULL it —
   * see `saveGoogleTokensToSupabase`.
   */
  access_token_expires_at?: string | null;
  last_refresh_failure_at?: string | null;
  refresh_attempts?: number;
  refresh_exhausted?: boolean;
  previous_refresh_token?: string | null;
  /**
   * SLOT-2 (the legacy linked second account) mirror of the columns above. The
   * engine addresses this slot as `<providerKey>:<email>:2` and keeps its health
   * strictly separate, so a dead second account can never latch the primary
   * grant. Declared here so the legacy writers below can NULL
   * `access_token_expires_at2` on a slot-2 write — same safe direction as slot 1.
   */
  access_token_expires_at2?: string | null;
  last_refresh_failure_at2?: string | null;
  refresh_attempts2?: number;
  refresh_exhausted2?: boolean;
  previous_refresh_token2?: string | null;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

export interface UserPreferences {
  id: string;
  email: string;
  timezone: string;
  working_hours: any;
  default_meeting_duration: number;
  calendar_settings: any;
  created_at: string;
  updated_at: string;
}

export interface BookingHistory {
  id: string;
  booker_email: string;
  bookee_email: string;
  subject: string;
  start_time: string;
  end_time: string;
  location?: string;
  description?: string;
  attendees?: any;
  google_event_id?: string;
  msft_event_id?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CalendarCache {
  id: string;
  email: string;
  provider: 'google' | 'microsoft';
  event_id: string;
  event_data: any;
  start_time: string;
  end_time: string;
  last_synced: string;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  email: string;
  action: string;
  provider?: string;
  details?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

// ============================================================================
// GOOGLE TOKENS FUNCTIONS (replaces DynamoDB functions)
// ============================================================================

/**
 * Get Google tokens from Supabase (replaces getTokenFromDynamoDB)
 */
export async function getGoogleTokensFromSupabase(email: string): Promise<GoogleTokens | null> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return null;
    }

    console.log(`[Supabase] Fetching Google tokens for: ${email}`);
    console.log(`[Supabase] Using service role key: ${!!supabaseServiceKey}`);
    
    const { data, error } = await supabase
      .from('google_tokens')
      .select('*')
      .eq('email', email)
      .maybeSingle();

    if (error) {
      console.error('Error fetching Google tokens:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
return null;
    }

    console.log(`[Supabase] Google tokens result for ${email}:`, {
      found: !!data,
      hasAccessToken: !!data?.access_token,
      hasRefreshToken: !!data?.refresh_token,
      dataPreview: data ? { email: data.email, id: data.id } : null
    });

    return data;
  } catch (error) {
    console.error('Error in getGoogleTokensFromSupabase:', error);
    
return null;
  }
}

/**
 * Save Google tokens to Supabase (replaces saveTokenInDynamoDB)
 */
export async function saveGoogleTokensToSupabase(
  email: string,
  accessToken: string,
  refreshToken?: string,
  isSecondAccount: boolean = false
): Promise<boolean> {
  try {
    console.log('[Supabase] saveGoogleTokensToSupabase called:', {
      email,
      isSecondAccount,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      supabaseInitialized: !!supabase,
    });

    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      console.error('[Supabase] Environment check:', {
        supabaseUrl: !!process.env.NEXT_PUBLIC_SUPABASE_URL,
        supabaseServiceKey: !!process.env.SUPABASE_SERVICE_ROLE_KEY,
        isDev: process.env.SUPABASE_ENV === 'dev' || process.env.NODE_ENV === 'development',
      });
      return false;
    }

    const tokenData: Partial<GoogleTokens> = {
      email,
      last_updated: new Date().toISOString(),
    };

    // This legacy writer does not know the new token's lifetime, so it MUST
    // clear the addressed slot's expiry rather than leave the previous token's
    // value behind. A stale-but-future expiry would make the ONE refresh engine
    // (apps/hub/lib/connections/unifiedtime) serve this token as "fresh" —
    // the single unsafe direction. NULL means "unknown" -> the engine refreshes.
    // Per-slot, because slot 2 has its OWN expiry column and the engine
    // addresses it independently as `<providerKey>:<email>:2`.
    if (isSecondAccount) {
      tokenData.access_token2 = accessToken.trim();
      if (refreshToken) tokenData.refresh_token2 = refreshToken.trim();
      tokenData.access_token_expires_at2 = null;
    } else {
      tokenData.access_token = accessToken.trim();
      if (refreshToken) tokenData.refresh_token = refreshToken.trim();
      tokenData.access_token_expires_at = null;
    }

    console.log('[Supabase] Checking if record exists for:', email);
    // Check if record exists
    const { data: existing, error: selectError } = await supabase
      .from('google_tokens')
      .select('id')
      .eq('email', email)
      .single();

    if (selectError && selectError.code !== 'PGRST116') {
      console.error('[Supabase] Error checking existing record:', selectError);
      return false;
    }

    if (existing) {
      console.log('[Supabase] Updating existing record for:', email);
      // Update existing record
      const { error } = await supabase
        .from('google_tokens')
        .update(tokenData)
        .eq('email', email);

      if (error) {
        console.error('Error updating Google tokens:', error);
        console.error('[Supabase] Update error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
        });
        
return false;
      }
      console.log('[Supabase] Successfully updated tokens for:', email);
    } else {
      console.log('[Supabase] Inserting new record for:', email);
      // Insert new record
      const { error } = await supabase
        .from('google_tokens')
        .insert([tokenData]);

      if (error) {
        console.error('Error inserting Google tokens:', error);
        console.error('[Supabase] Insert error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
        });
        
return false;
      }
      console.log('[Supabase] Successfully inserted tokens for:', email);
    }

    console.log(`[Audit Log] Saved Google token for ${isSecondAccount ? 'second' : 'main'} account of user ${email} at ${new Date().toISOString()}`);
    
return true;
  } catch (error) {
    console.error('Error in saveGoogleTokensToSupabase:', error);
    console.error('[Supabase] Exception details:', {
      message: (error as Error).message,
      stack: (error as Error).stack,
    });
    
return false;
  }
}

// ============================================================================
// MICROSOFT TOKENS FUNCTIONS
// ============================================================================

/**
 * Get Microsoft tokens from Supabase
 */
export async function getMsftTokensFromSupabase(email: string): Promise<MsftTokens | null> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return null;
    }

    const { data, error } = await supabase
      .from('msft_tokens')
      .select('*')
      .eq('email', email)
      .maybeSingle();

    if (error) {
      console.error('Error fetching Microsoft tokens:', error);
      
return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getMsftTokensFromSupabase:', error);
    
return null;
  }
}

/**
 * Save Microsoft tokens to Supabase
 */
export async function saveMsftTokensToSupabase(
  email: string,
  accessToken: string,
  refreshToken?: string,
  isSecondAccount: boolean = false
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return false;
    }

    const tokenData: Partial<MsftTokens> = {
      email,
      last_updated: new Date().toISOString(),
    };

    // Same per-slot safe-direction rule as `saveGoogleTokensToSupabase`: this
    // writer does not know the new token's lifetime, so it NULLs the addressed
    // slot's expiry. Leaving a previous token's future expiry behind would make
    // the ONE refresh engine serve a dead token as "fresh".
    if (isSecondAccount) {
      tokenData.access_token2 = accessToken.trim();
      if (refreshToken) tokenData.refresh_token2 = refreshToken.trim();
      tokenData.access_token_expires_at2 = null;
    } else {
      tokenData.access_token = accessToken.trim();
      if (refreshToken) tokenData.refresh_token = refreshToken.trim();
      tokenData.access_token_expires_at = null;
    }

    // Check if record exists
    const { data: existing } = await supabase
      .from('msft_tokens')
      .select('id')
      .eq('email', email)
      .single();

    if (existing) {
      // Update existing record
      const { error } = await supabase
        .from('msft_tokens')
        .update(tokenData)
        .eq('email', email);

      if (error) {
        console.error('Error updating Microsoft tokens:', error);
        
return false;
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from('msft_tokens')
        .insert([tokenData]);

      if (error) {
        console.error('Error inserting Microsoft tokens:', error);
        
return false;
      }
    }

    console.log(`[Audit Log] Saved Microsoft token for ${isSecondAccount ? 'second' : 'main'} account of user ${email} at ${new Date().toISOString()}`);
    
return true;
  } catch (error) {
    console.error('Error in saveMsftTokensToSupabase:', error);
    
return false;
  }
}

// ============================================================================
// USER PREFERENCES FUNCTIONS
// ============================================================================

/**
 * Get user preferences from Supabase
 */
export async function getUserPreferencesFromSupabase(email: string): Promise<UserPreferences | null> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return null;
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      console.error('Error fetching user preferences:', error);
      
return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getUserPreferencesFromSupabase:', error);
    
return null;
  }
}

/**
 * Save user preferences to Supabase
 */
export async function saveUserPreferencesToSupabase(
  email: string,
  preferences: Partial<UserPreferences>
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return false;
    }

    const { data: existing } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('email', email)
      .single();

    if (existing) {
      // Update existing record
      const { error } = await supabase
        .from('user_preferences')
        .update(preferences)
        .eq('email', email);

      if (error) {
        console.error('Error updating user preferences:', error);
        
return false;
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from('user_preferences')
        .insert([{ email, ...preferences }]);

      if (error) {
        console.error('Error inserting user preferences:', error);
        
return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error in saveUserPreferencesToSupabase:', error);
    
return false;
  }
}

// ============================================================================
// BOOKING HISTORY FUNCTIONS
// ============================================================================

/**
 * Save booking to history
 */
export async function saveBookingToHistory(booking: Omit<BookingHistory, 'id' | 'created_at' | 'updated_at'>): Promise<string | null> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return null;
    }

    const { data, error } = await supabase
      .from('booking_history')
      .insert([booking])
      .select('id')
      .single();

    if (error) {
      console.error('Error saving booking to history:', error);
      
return null;
    }

    return data.id;
  } catch (error) {
    console.error('Error in saveBookingToHistory:', error);
    
return null;
  }
}

/**
 * Get booking history for a user
 */
export async function getBookingHistory(email: string): Promise<BookingHistory[]> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return [];
    }

    const { data, error } = await supabase
      .from('booking_history')
      .select('*')
      .or(`booker_email.eq.${email},bookee_email.eq.${email}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching booking history:', error);
      
return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getBookingHistory:', error);
    
return [];
  }
}

// ============================================================================
// CALENDAR CACHE FUNCTIONS
// ============================================================================

/**
 * Save calendar event to cache
 */
export async function saveCalendarEventToCache(
  email: string,
  provider: 'google' | 'microsoft',
  eventId: string,
  eventData: any,
  startTime: string,
  endTime: string
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return false;
    }

    const cacheData: Partial<CalendarCache> = {
      email,
      provider,
      event_id: eventId,
      event_data: eventData,
      start_time: startTime,
      end_time: endTime,
      last_synced: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('calendar_cache')
      .upsert([cacheData], { onConflict: 'email,provider,event_id' });

    if (error) {
      console.error('Error saving calendar event to cache:', error);
      
return false;
    }

    return true;
  } catch (error) {
    console.error('Error in saveCalendarEventToCache:', error);
    
return false;
  }
}

/**
 * Get cached calendar events
 */
export async function getCachedCalendarEvents(
  email: string,
  provider: 'google' | 'microsoft',
  startTime?: string,
  endTime?: string
): Promise<CalendarCache[]> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return [];
    }

    let query = supabase
      .from('calendar_cache')
      .select('*')
      .eq('email', email)
      .eq('provider', provider);

    if (startTime) {
      query = query.gte('start_time', startTime);
    }
    if (endTime) {
      query = query.lte('end_time', endTime);
    }

    const { data, error } = await query.order('start_time', { ascending: true });

    if (error) {
      console.error('Error fetching cached calendar events:', error);
      
return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getCachedCalendarEvents:', error);
    
return [];
  }
}

// ============================================================================
// AUDIT LOG FUNCTIONS
// ============================================================================

/**
 * Log an audit event
 */
export async function logAuditEvent(
  email: string,
  action: string,
  provider?: string,
  details?: any,
  ipAddress?: string,
  userAgent?: string
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return false;
    }

    const auditData: Partial<AuditLog> = {
      email,
      action,
      provider,
      details,
      ip_address: ipAddress,
      user_agent: userAgent,
    };

    const { error } = await supabase
      .from('audit_log')
      .insert([auditData]);

    if (error) {
      console.error('Error logging audit event:', error);
      
return false;
    }

    return true;
  } catch (error) {
    console.error('Error in logAuditEvent:', error);
    
return false;
  }
}

// ============================================================================
// ACCOUNT MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Clear all user data (for account reset)
 */
export async function clearUserData(email: string): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      return false;
    }

    // Clear tokens
    await supabase.from('google_tokens').delete().eq('email', email);
    await supabase.from('msft_tokens').delete().eq('email', email);
    
    // Clear preferences
    await supabase.from('user_preferences').delete().eq('email', email);
    
    // Clear calendar cache
    await supabase.from('calendar_cache').delete().eq('email', email);
    
    // Note: We keep booking_history and audit_log for compliance
    
    console.log(`[Audit Log] Cleared all data for user ${email} at ${new Date().toISOString()}`);
    
return true;
  } catch (error) {
    console.error('Error in clearUserData:', error);
    
return false;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all tokens for a user (Google and Microsoft)
 */
export async function getAllUserTokens(email: string): Promise<{
  google: GoogleTokens | null;
  microsoft: MsftTokens | null;
}> {
  const [googleTokens, msftTokens] = await Promise.all([
    getGoogleTokensFromSupabase(email),
    getMsftTokensFromSupabase(email),
  ]);

  return {
    google: googleTokens,
    microsoft: msftTokens,
  };
}

/**
 * Check if user has any tokens
 */
export async function userHasTokens(email: string): Promise<boolean> {
  const tokens = await getAllUserTokens(email);
  
return !!(tokens.google || tokens.microsoft);
}

/**
 * Delete Google tokens for a user
 */
export async function deleteGoogleTokensFromSupabase(userEmail: string): Promise<any> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase
      .from('google_tokens')
      .delete()
      .eq('email', userEmail)
      .select();

    if (error) {
      console.error('Error deleting Google tokens:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error deleting Google tokens:', error);
    throw error;
  }
}

/**
 * Delete Microsoft tokens for a user
 */
export async function deleteMsftTokensFromSupabase(userEmail: string): Promise<any> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized - environment variables missing');
      throw new Error('Supabase client not initialized');
    }

    DebugLogger.logDatabase('SUPABASE MSFT DELETE DEBUG', {
      userEmail,
      operation: 'Deleting Microsoft tokens'
    });
    
    const { data, error } = await supabase
      .from('msft_tokens')
      .delete()
      .eq('email', userEmail)
      .select();

    console.log('Supabase delete result - data:', data);
    console.log('Supabase delete result - error:', error);

    if (error) {
      console.error('Error deleting Microsoft tokens:', error);
      throw error;
    }

    console.log('Microsoft tokens deleted successfully:', data);
    
return data;
  } catch (error) {
    console.error('Error deleting Microsoft tokens:', error);
    throw error;
  }
}
